<template>
	<section class="step" :class="[`step--${imageSide}`]" :style="sectionVars">
		<div class="container step__container">
			<h2 v-if="title" class="step__title headline-l" v-html="title"></h2>

			<div class="row step__row">
				<!-- IMAGE (left side) -->
				<div
					v-if="imageSide === 'left'"
					class="col-12 col-md-5 order-1 order-md-1"
				>
					<div class="step__imageWrap">
						<img
							v-if="imageSrc"
							:src="imageSrc"
							:alt="imageAlt"
							class="step__fixed-image"
							style="z-index: 1"
						/>
					</div>
				</div>

				<!-- LIST -->
				<div class="col-12 col-md-7 step__listCol order-2 order-md-2">
					<div class="step__list">
						<div
							v-for="(item, idx) in items"
							:key="idx"
							class="step__item"
							:style="getItemStyle(item)"
						>
							<div class="step__itemInner">
								<div class="step__text">
									<div
										v-if="item.title"
										class="headline-l step__itemTitle"
										v-html="item.title"
									></div>
									<div
										v-if="item.text"
										class="step__itemText"
										v-html="item.text"
									></div>
								</div>
							</div>
						</div>
					</div>

					<div v-if="$slots.footer" class="step__footer">
						<slot name="footer" />
					</div>
				</div>

				<!-- IMAGE (right side) -->
				<div
					v-if="imageSide === 'right'"
					class="col-12 col-md-5 order-1 order-md-3"
				>
					<div class="step__imageWrap">
						<img
							v-if="imageSrc"
							:src="imageSrc"
							:alt="imageAlt"
							class="step__fixed-image"
							style="z-index: 1"
						/>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script>
export default {
	name: 'StepByStepSection',
	props: {
		title: { type: String, default: '' },

		backgroundColor: { type: String, default: '#E8E8FF' },
		backgroundImage: { type: String, default: '' }, // optional
		backgroundImageMobile: { type: String, default: '' }, // optional - mobile specific background
		backgroundPosition: { type: String, default: 'center' },
		backgroundSize: { type: String, default: 'cover' },

		imageSrc: { type: String, required: true },
		imageAlt: { type: String, default: 'Phone preview' },
		imageSide: {
			type: String,
			default: 'left', // 'left' | 'right'
			validator: (v) => ['left', 'right'].includes(v),
		},

		items: {
			type: Array,
			default: () => [],
			// item: { icon?: string, title: string, text: string }
		},

		// Item gradient colors
		itemGradientStart: { type: String, default: '#e8e8ff' },
		itemGradientEnd: { type: String, default: '#c2c5ff' },

		textColor: { type: String, default: '#050535' },
		mutedTextColor: { type: String, default: '#050535' },
	},
	computed: {
		sectionVars() {
			const mobileImage = this.backgroundImageMobile || this.backgroundImage;
			return `
        --bg: ${this.backgroundColor};
        --bg-image: ${
					this.backgroundImage ? `url('${this.backgroundImage}')` : 'none'
				};
        --bg-image-mobile: ${mobileImage ? `url('${mobileImage}')` : 'none'};
        --bg-pos: ${this.backgroundPosition};
        --bg-size: ${this.backgroundSize};
        --text: ${this.textColor};
        --muted: ${this.mutedTextColor};
        --item-gradient-start: ${this.itemGradientStart};
        --item-gradient-end: ${this.itemGradientEnd};
      `;
		},
	},
	methods: {
		getItemStyle(item) {
			if (item.gradientStart || item.gradientEnd) {
				const start = item.gradientStart || this.itemGradientStart;
				const end = item.gradientEnd || this.itemGradientEnd;
				const direction = this.imageSide === 'right' ? '270deg' : '90deg';
				return {
					background: `linear-gradient(${direction}, ${start} 0%, ${end} 100%)`,
				};
			}
			return {};
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/global.scss';

.step {
	position: relative;
	background-color: var(--bg);

	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-image: var(--bg-image);
		background-repeat: no-repeat;
		background-position: var(--bg-pos);
		background-size: var(--bg-size);
		opacity: 0.08;
		pointer-events: none;
		z-index: 0;

		@media (max-width: $breakpoint-sm) {
			background-image: var(--bg-image-mobile);
			opacity: 0.1;
		}
	}
}

.step__container {
	position: relative;
	z-index: 1;
}

.step__title {
	text-align: center;
	font-weight: 800;
	color: var(--text);
	font-size: 38px;
	padding-top: 62px;
	line-height: 50px;
	text-transform: uppercase;

	@media (max-width: $breakpoint-sm) {
		padding-top: 45px;
	}
}

.step__row {
	align-items: center;
}

.step__imageWrap {
	display: flex;
	justify-content: center;
	align-items: center;
	width: 100%;
	min-height: 612px;

	@media (max-width: $breakpoint-sm) {
		min-height: auto;
		margin-bottom: -120px;
		z-index: 0;
	}
}

.step__fixed-image {
	width: auto;
	height: 625px;
	object-fit: contain;
	display: block;
	margin: 80px auto 0;
	max-width: 100%;

	@media (max-width: $breakpoint-sm) {
		height: 350px;
		margin-top: 30px;
	}
}

.step__list {
	display: grid;
	gap: 15px;

	@media (max-width: $breakpoint-sm) {
		position: relative;
		z-index: 1;
	}
}

.step__listCol {
	@media (max-width: $breakpoint-sm) {
		padding-left: 0;
		padding-right: 0;
	}
}

.step__item {
	position: relative;
	padding: 18px 18px 18px 32px;
	min-height: 113px;
	display: flex;
	align-items: center;
	background: linear-gradient(
		90deg,
		var(--item-gradient-start) -20%,
		var(--item-gradient-end) 100%
	);
	margin-left: 0;
	margin-right: 0;

	@media (max-width: $breakpoint-sm) {
		padding: 16px;
		margin-left: 0;
		margin-right: 0;
		min-height: auto;
	}

	@media (min-width: 768px) {
		margin-left: -60px;
	}

	@media (min-width: 1000px) {
		margin-left: -120px;
	}

	@media (min-width: 1200px) {
		margin-left: -145px;
	}

	.step--right & {
		background: linear-gradient(
			270deg,
			var(--item-gradient-start) -10%,
			var(--item-gradient-end) 100%
		);
		padding: 18px 32px 18px 18px;

		@media (max-width: $breakpoint-sm) {
			padding: 16px;
		}

		@media (min-width: 768px) {
			margin-left: 0;
			margin-right: -60px;
		}

		@media (min-width: 1000px) {
			margin-right: -120px;
		}

		@media (min-width: 1200px) {
			margin-right: -145px;
		}
	}
}

.step__itemInner {
	display: flex;
	gap: 14px;
	align-items: flex-start;

	@media (max-width: $breakpoint-sm) {
		justify-content: center;
	}
}

.step__itemTitle {
	color: var(--text);
	font-size: 18px;
	letter-spacing: 0.02em;
	text-transform: uppercase;
	margin-bottom: 4px;
	line-height: 24px;

	@media (max-width: $breakpoint-sm) {
		font-family: 'HK Nova', sans-serif;
		font-weight: 700;
		font-size: 18px;
		line-height: 24px;
		letter-spacing: 0;
		text-align: center;
		text-transform: uppercase;
		margin-bottom: 0;
	}
}

.step__itemText {
	color: var(--muted);
	font-size: 18px;
	line-height: 24px;

	@media (max-width: $breakpoint-sm) {
		letter-spacing: 0;
		text-align: center;
	}
}

.step__footer {
	margin-top: 18px;
}
</style>
